import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  GameId,
  LoungeClientMessage,
  LoungeServerMessage,
  LoungeState,
  PlayerId,
} from '@ofc/shared';
import { GameConnection, type ConnStatus } from '@ofc/ui';

export interface LoungeActions {
  pick(game: GameId): void;
  recall(): void;
  kick(playerId: PlayerId): void;
  transferHost(playerId: PlayerId): void;
}

/**
 * Keeps this phone in a game night.
 *
 * Held open by the app for as long as you are anywhere in the night — the
 * lounge itself or any game it started — so when the host picks the next game,
 * `onGo` fires wherever you happen to be.
 */
export function useLounge(
  code: string | null,
  name: string,
  sessionToken: string | undefined,
  onGo: (game: GameId, roomCode: string) => void,
  opts: { spectator?: boolean; onHome?: () => void } = {},
) {
  const [state, setState] = useState<LoungeState | null>(null);
  const [you, setYou] = useState<PlayerId | null>(null);
  const [status, setStatus] = useState<ConnStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const connRef = useRef<GameConnection<LoungeClientMessage, LoungeServerMessage> | null>(null);
  const goRef = useRef(onGo);
  goRef.current = onGo;
  const homeRef = useRef(opts.onHome);
  homeRef.current = opts.onHome;

  useEffect(() => {
    if (!code) {
      setState(null);
      return;
    }
    const conn = new GameConnection<LoungeClientMessage, LoungeServerMessage>({
      game: 'lounge',
      roomCode: code,
      name,
      sessionToken,
      spectator: opts.spectator,
      handlers: {
        onStatus: setStatus,
        onIdentity: (identity) => setYou(identity.playerId),
        onMessage: (msg) => {
          switch (msg.t) {
            case 'welcome':
              if (opts.spectator) setYou(null);
              setState(msg.state);
              return;
            case 'state':
              setState(msg.state);
              return;
            case 'go':
              goRef.current(msg.game, msg.roomCode);
              return;
            case 'home':
              homeRef.current?.();
              return;
            case 'error':
              setError(msg.message);
              return;
            default:
              return;
          }
        },
      },
    });
    connRef.current = conn;
    conn.start();
    return () => {
      conn.stop();
      connRef.current = null;
    };
    // The name only matters for the first hello.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, sessionToken, opts.spectator]);

  const send = useCallback((msg: LoungeClientMessage) => connRef.current?.send(msg), []);

  const actions = useMemo<LoungeActions>(
    () => ({
      pick: (game) => send({ t: 'pick', game }),
      recall: () => send({ t: 'recall' }),
      kick: (playerId) => send({ t: 'kick', playerId }),
      transferHost: (playerId) => send({ t: 'transferHost', playerId }),
    }),
    [send],
  );

  const isHost = state !== null && you !== null && state.hostId === you;

  return { state, you, status, error, setError, actions, isHost };
}

export type Lounge = ReturnType<typeof useLounge>;
